import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Plus,
  Copy,
  Eye,
  EyeOff,
  Save,
  Trash2,
  FileUp,
  ChevronDown,
  ChevronRight,
  Globe,
  Settings2,
  SlidersHorizontal,
  Layout,
  KeyRound,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormField, FormInput, FormSelect, FormTextarea } from '@/components/form';
import { Button } from '@/components/ui/button';
import { scrollToFirstError, mergeApiErrors } from '@/lib/form-utils';

import { JSONImportDialog } from './JSONImportDialog';
import { ParameterEditor, type ParameterDefinition } from './ParameterEditor';
import { TestToolDialog } from './TestToolDialog';

import type {
  CreateCustomToolDto,
  JsonSchemaProperty,
  TestCustomToolDto,
  TokenAuth,
} from '@repo/shared-types';

const HTTP_METHOD_OPTIONS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
] as const;

const customToolFormSchema = z.object({
  name: z
    .string()
    .min(2, 'Tool name must be at least 2 characters')
    .regex(/^[a-z_][a-z0-9_]*$/, 'Must be snake_case (lowercase, underscores only)'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  http_method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  url: z.string().url('Must be a valid URL'),
  output_path: z.string().optional(),
  error_path: z.string().optional(),
  selected_fields: z.string().optional(),
  is_external: z.boolean(),
  idempotency: z.enum(['none', 'per_call', 'per_call_and_args']),
  display_enabled: z.boolean(),
  display_type: z.enum(['carousel', 'cards', 'notice', 'badges', 'links', 'files']),
  display_array_path: z.string().optional(),
  display_field_image: z.string().optional(),
  display_field_title: z.string().optional(),
  display_field_description: z.string().optional(),
  display_field_price: z.string().optional(),
  display_field_link: z.string().optional(),
  display_field_badge: z.string().optional(),
  display_field_file_type: z.string().optional(),
  display_fixed: z.boolean().optional(),
  display_fallback_image: z.string().optional(),
  whatsapp_link_base: z.string().optional(),
  whatsapp_template_name: z.string().optional(),
});

type CustomToolFormValues = z.infer<typeof customToolFormSchema>;

interface CustomToolFormProps {
  initialValues?: CreateCustomToolDto;
  onSubmit?: (values: CreateCustomToolDto) => void;
  errors?: Array<{ field: string; message: string }>;
  isLoading?: boolean;
  submitLabel?: string;
}

// Collapsible section component
const Section = ({
  title,
  icon: Icon,
  defaultOpen = true,
  badge,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  defaultOpen?: boolean;
  badge?: string | number;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <ChevronDown size={16} className="text-muted-foreground" />
        ) : (
          <ChevronRight size={16} className="text-muted-foreground" />
        )}
        <Icon size={16} className="text-muted-foreground" />
        <span>{title}</span>
        {badge !== undefined && (
          <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
            {badge}
          </span>
        )}
      </button>
      {isOpen && <div className="border-t px-4 pt-3 pb-4">{children}</div>}
    </div>
  );
};

export const CustomToolForm = ({
  initialValues,
  onSubmit,
  errors: apiErrors = [],
  isLoading = false,
  submitLabel = 'Create Tool',
}: CustomToolFormProps) => {
  const [parameters, setParameters] = useState<ParameterDefinition[]>([]);
  const [bodyTemplate, setBodyTemplate] = useState<unknown>(
    initialValues?.parameters_schema?.body_template ?? null,
  );
  const [headerInputs, setHeaderInputs] = useState<Array<{ key: string; value: string }>>([]);

  // Two-step bearer-token auth (token_auth). Simple scalar fields use local
  // useState; token_headers mirrors the request `headerInputs` key/value editor.
  // token_query / token_body / refresh_status_codes have no visible UI yet but
  // are preserved verbatim across import → edit → submit so they are never lost.
  const [enableTokenAuth, setEnableTokenAuth] = useState(!!initialValues?.token_auth);
  const [tokenUrl, setTokenUrl] = useState(initialValues?.token_auth?.token_url ?? '');
  const [tokenMethod, setTokenMethod] = useState<CustomToolFormValues['http_method']>(
    (initialValues?.token_auth?.token_method as CustomToolFormValues['http_method']) ?? 'GET',
  );
  const [tokenPath, setTokenPath] = useState(initialValues?.token_auth?.token_path ?? '');
  const [expiresInPath, setExpiresInPath] = useState(
    initialValues?.token_auth?.expires_in_path ?? '',
  );
  const [injectHeader, setInjectHeader] = useState(initialValues?.token_auth?.inject_header ?? '');
  const [injectTemplate, setInjectTemplate] = useState(
    initialValues?.token_auth?.inject_template ?? '',
  );
  const [tokenHeaderInputs, setTokenHeaderInputs] = useState<Array<{ key: string; value: string }>>(
    [],
  );
  // Preserved-but-not-edited token_auth fields (carried through round-trip).
  const [tokenExtra, setTokenExtra] = useState<{
    token_query?: Record<string, string>;
    token_body?: unknown;
    refresh_status_codes?: number[];
  }>({
    token_query: initialValues?.token_auth?.token_query,
    token_body: initialValues?.token_auth?.token_body,
    refresh_status_codes: initialValues?.token_auth?.refresh_status_codes,
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showFullImport, setShowFullImport] = useState(false);
  const [fullImportJson, setFullImportJson] = useState('');
  const [fullImportError, setFullImportError] = useState('');
  const [newParamIndex, setNewParamIndex] = useState<number | null>(null);
  const parametersEndRef = useRef<HTMLDivElement>(null);

  // selected_fields can hold plain string paths AND computed-field objects
  // ({ path, as, substr, add }). Split them: strings go in the comma box,
  // objects into the editable computed-fields UI below. (Joining objects into
  // the string box would corrupt them into "[object Object]".)
  const initialSelectedFields = (initialValues?.response_mapping?.selected_fields ?? []) as Array<
    string | { path: string; as?: string; substr?: [number, number]; add?: number }
  >;
  const initialStringFields = initialSelectedFields.filter(
    (f): f is string => typeof f === 'string',
  );
  const initialComputedFields = initialSelectedFields
    .filter(
      (f): f is { path: string; as?: string; substr?: [number, number]; add?: number } =>
        typeof f === 'object' && f !== null,
    )
    .map((f) => ({
      path: f.path ?? '',
      as: f.as ?? '',
      substrStart: f.substr?.[0]?.toString() ?? '',
      substrEnd: f.substr?.[1]?.toString() ?? '',
      add: f.add?.toString() ?? '',
    }));

  const defaultValues: CustomToolFormValues = {
    name: initialValues?.name ?? '',
    description: initialValues?.description ?? '',
    http_method: (initialValues?.http_method as CustomToolFormValues['http_method']) ?? 'GET',
    url: initialValues?.url ?? '',
    output_path: initialValues?.response_mapping?.output_path ?? '',
    error_path: initialValues?.response_mapping?.error_path ?? '',
    selected_fields: initialStringFields.join(', '),
    is_external: initialValues?.is_external ?? false,
    idempotency: initialValues?.idempotency ?? 'none',
    display_enabled: !!initialValues?.display_config,
    display_type:
      (initialValues?.display_config?.type as CustomToolFormValues['display_type']) ?? 'carousel',
    display_array_path: initialValues?.display_config?.array_path ?? '',
    display_field_image: initialValues?.display_config?.fields?.image ?? '',
    display_field_title: initialValues?.display_config?.fields?.title ?? '',
    display_field_description: initialValues?.display_config?.fields?.description ?? '',
    display_field_price: initialValues?.display_config?.fields?.price ?? '',
    display_field_link: initialValues?.display_config?.fields?.link ?? '',
    display_field_badge: initialValues?.display_config?.fields?.badge ?? '',
    display_field_file_type: initialValues?.display_config?.fields?.file_type ?? '',
    display_fixed: initialValues?.display_config?.fixed ?? false,
    display_fallback_image: initialValues?.display_config?.fallback_image ?? '',
    whatsapp_link_base: initialValues?.display_config?.whatsapp_link_base ?? '',
    whatsapp_template_name: initialValues?.whatsapp_template_name ?? '',
  };

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<CustomToolFormValues>({
    resolver: zodResolver(customToolFormSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const urlValue = watch('url');

  const pathVariables = useMemo(() => {
    if (!urlValue) return [];
    const matches = urlValue.match(/\{([^}]+)\}/g);
    return matches ? matches.map((match) => match.slice(1, -1)) : [];
  }, [urlValue]);

  const [sampleFilterFields, setSampleFilterFields] = useState<string[]>([]);
  const [detectedTopLevelKeys, setDetectedTopLevelKeys] = useState<string[]>([]);
  const [computedFields, setComputedFields] = useState(initialComputedFields);

  // Combine the comma-box string paths with the editable computed fields into
  // the response_mapping.selected_fields array (string | { path, as, substr, add }).
  const buildSelectedFields = (raw?: string) => {
    const strings = (raw ?? '')
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);
    const computed = computedFields
      .filter((c) => c.path.trim())
      .map((c) => ({
        path: c.path.trim(),
        ...(c.as.trim() && { as: c.as.trim() }),
        ...(c.substrStart.trim() !== '' &&
          c.substrEnd.trim() !== '' && {
            substr: [Number(c.substrStart), Number(c.substrEnd)] as [number, number],
          }),
        ...(c.add.trim() !== '' && !Number.isNaN(Number(c.add)) && { add: Number(c.add) }),
      }));
    const all = [...strings, ...computed];
    return all.length ? all : undefined;
  };

  const getTestToolConfig = useCallback((): TestCustomToolDto => {
    const schema = {
      ...convertParametersToSchema(parameters),
      ...(bodyTemplate ? { body_template: bodyTemplate } : {}),
    };
    return {
      http_method: watch('http_method'),
      url: watch('url'),
      headers: headerInputs.reduce(
        (acc, h) => {
          if (h.key.trim() && h.value.trim()) acc[h.key.trim()] = h.value.trim();
          return acc;
        },
        {} as Record<string, string>,
      ),
      parameters_schema: schema,
      output_path: watch('output_path') || undefined,
      selected_fields: watch('selected_fields')
        ?.split(',')
        .map((f) => f.trim())
        .filter(Boolean),
    };
  }, [parameters, headerInputs, watch]);

  const handleFieldSelectAsFilter = useCallback((fieldName: string) => {
    setParameters((prev) => [
      ...prev,
      {
        name: `filter_${fieldName}`,
        type: 'string' as const,
        description: `Filter results by ${fieldName}`,
        required: false,
        in: 'filter' as const,
        filter_config: {
          target_field: fieldName,
          operator: '>=' as const,
        },
      },
    ]);
  }, []);

  // Initialize from initial values
  useEffect(() => {
    if (initialValues?.headers) {
      const headerEntries = Object.entries(initialValues.headers).map(([key, value]) => ({
        key,
        value: String(value),
      }));
      setHeaderInputs(headerEntries);
    }

    if (initialValues?.parameters_schema?.properties) {
      const params = convertSchemaToParameters(initialValues.parameters_schema);
      setParameters(params);
    }

    const ta = initialValues?.token_auth;
    setEnableTokenAuth(!!ta);
    if (ta) {
      setTokenUrl(ta.token_url ?? '');
      setTokenMethod((ta.token_method as CustomToolFormValues['http_method']) ?? 'GET');
      setTokenPath(ta.token_path ?? '');
      setExpiresInPath(ta.expires_in_path ?? '');
      setInjectHeader(ta.inject_header ?? '');
      setInjectTemplate(ta.inject_template ?? '');
      setTokenHeaderInputs(
        Object.entries(ta.token_headers ?? {}).map(([key, value]) => ({
          key,
          value: String(value),
        })),
      );
      setTokenExtra({
        token_query: ta.token_query,
        token_body: ta.token_body,
        refresh_status_codes: ta.refresh_status_codes,
      });
    }
  }, [initialValues]);

  // Merge API errors
  useEffect(() => {
    if (apiErrors.length > 0) {
      mergeApiErrors(apiErrors, setError);
    }
  }, [apiErrors, setError]);

  // Scroll to new parameter when added
  useEffect(() => {
    if (newParamIndex !== null) {
      parametersEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setNewParamIndex(null);
    }
  }, [newParamIndex]);

  const addParameter = () => {
    const newParam: ParameterDefinition = {
      name: '',
      type: 'string',
      description: '',
      required: false,
      in: 'body',
    };
    setParameters((prev) => [...prev, newParam]);
    setNewParamIndex(parameters.length);
  };

  const removeParameter = (index: number) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const updateParameter = (index: number, updated: ParameterDefinition) => {
    const newParams = [...parameters];
    newParams[index] = updated;
    setParameters(newParams);
  };

  const addHeaderRow = () => {
    setHeaderInputs([...headerInputs, { key: '', value: '' }]);
  };

  const removeHeaderRow = (index: number) => {
    setHeaderInputs(headerInputs.filter((_, i) => i !== index));
  };

  const updateHeaderRow = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...headerInputs];
    updated[index] = { ...updated[index], [field]: value };
    setHeaderInputs(updated);
  };

  const addTokenHeaderRow = () => {
    setTokenHeaderInputs([...tokenHeaderInputs, { key: '', value: '' }]);
  };

  const removeTokenHeaderRow = (index: number) => {
    setTokenHeaderInputs(tokenHeaderInputs.filter((_, i) => i !== index));
  };

  const updateTokenHeaderRow = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...tokenHeaderInputs];
    updated[index] = { ...updated[index], [field]: value };
    setTokenHeaderInputs(updated);
  };

  // Build the token_auth object from state (or null when disabled). Shared by
  // the JSON preview and the submit handler. Mirrors how display_config is
  // conditionally assembled — only keys with values are emitted.
  const buildTokenAuth = (): TokenAuth | null => {
    if (!enableTokenAuth) return null;
    const tokenHeadersObj: Record<string, string> = {};
    tokenHeaderInputs.forEach((h) => {
      if (h.key.trim() && h.value.trim()) tokenHeadersObj[h.key.trim()] = h.value.trim();
    });
    return {
      token_url: tokenUrl.trim(),
      token_method: tokenMethod,
      ...(Object.keys(tokenHeadersObj).length > 0 && { token_headers: tokenHeadersObj }),
      ...(tokenExtra.token_query &&
        Object.keys(tokenExtra.token_query).length > 0 && {
          token_query: tokenExtra.token_query,
        }),
      ...(tokenExtra.token_body !== undefined && { token_body: tokenExtra.token_body }),
      ...(tokenPath.trim() && { token_path: tokenPath.trim() }),
      ...(expiresInPath.trim() && { expires_in_path: expiresInPath.trim() }),
      ...(injectHeader.trim() && { inject_header: injectHeader.trim() }),
      ...(injectTemplate.trim() && { inject_template: injectTemplate.trim() }),
      ...(tokenExtra.refresh_status_codes &&
        tokenExtra.refresh_status_codes.length > 0 && {
          refresh_status_codes: tokenExtra.refresh_status_codes,
        }),
    };
  };

  // Convert parameters to JSON Schema
  const convertParametersToSchema = (
    params: ParameterDefinition[],
  ): {
    type: 'object';
    properties: Record<string, JsonSchemaProperty>;
    required: string[];
  } => {
    const properties: Record<string, JsonSchemaProperty> = {};
    const required: string[] = [];

    params.forEach((param) => {
      if (!param.name) return;
      const prop = convertParameterToProperty(param);
      properties[param.name] = prop;
      if (param.required && !param.isFixed) {
        required.push(param.name);
      }
    });

    return { type: 'object' as const, properties, required };
  };

  const convertParameterToProperty = (param: ParameterDefinition): JsonSchemaProperty => {
    const prop: JsonSchemaProperty = {
      type: param.type,
      description: param.description,
    };

    if (param.enum && param.enum.length > 0) prop.enum = param.enum;
    if (param.isFixed && param.fixedValue !== undefined) prop.fixed_value = param.fixedValue;
    if (param.in) prop.in = param.in;
    if (param.path_variable) prop.path_variable = param.path_variable;
    if (param.query_param) prop.query_param = param.query_param;
    if (param.filter_config) prop.filter_config = param.filter_config;

    if (param.type === 'array' && param.items) {
      const itemsSchema: Record<string, unknown> = { type: param.items.type };
      if (param.items.properties && param.items.properties.length > 0) {
        const nestedProps: Record<string, JsonSchemaProperty> = {};
        const nestedRequired: string[] = [];
        param.items.properties.forEach((nestedParam) => {
          if (nestedParam.name) {
            nestedProps[nestedParam.name] = convertParameterToProperty(nestedParam);
            if (nestedParam.required && !nestedParam.isFixed) nestedRequired.push(nestedParam.name);
          }
        });
        itemsSchema.properties = nestedProps;
        if (nestedRequired.length > 0) itemsSchema.required = nestedRequired;
      }
      prop.items = itemsSchema;
    }

    if (param.type === 'object' && param.properties && param.properties.length > 0) {
      const nestedProps: Record<string, JsonSchemaProperty> = {};
      const nestedRequired: string[] = [];
      param.properties.forEach((nestedParam) => {
        if (nestedParam.name) {
          nestedProps[nestedParam.name] = convertParameterToProperty(nestedParam);
          if (nestedParam.required && !nestedParam.isFixed) nestedRequired.push(nestedParam.name);
        }
      });
      prop.properties = nestedProps;
      if (nestedRequired.length > 0) prop.required = nestedRequired;
    }

    return prop;
  };

  const convertPropertyToParameter = (
    name: string,
    prop: Record<string, unknown>,
    parentRequired: string[] = [],
  ): ParameterDefinition => {
    const param: ParameterDefinition = {
      name,
      type: prop.type as ParameterDefinition['type'],
      description: (prop.description as string) || '',
      required: parentRequired?.includes(name) || false,
      enum: prop.enum as string[],
      isFixed: prop.fixed_value !== undefined,
      fixedValue: prop.fixed_value,
      in: (prop.in as 'path' | 'query' | 'body' | 'filter' | 'display') || 'body',
      path_variable: prop.path_variable as string,
      query_param: prop.query_param as string,
      filter_config: prop.filter_config as ParameterDefinition['filter_config'],
    };

    if (prop.type === 'array' && prop.items) {
      const items = prop.items as Record<string, unknown>;
      param.items = {
        type: items.type as 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array',
        properties: items.properties
          ? Object.entries(items.properties as Record<string, Record<string, unknown>>).map(
              ([nestedName, nestedProp]) =>
                convertPropertyToParameter(nestedName, nestedProp, items.required as string[]),
            )
          : [],
        required: items.required as string[],
      };
    }

    if (prop.type === 'object' && prop.properties) {
      param.properties = Object.entries(
        prop.properties as Record<string, Record<string, unknown>>,
      ).map(([nestedName, nestedProp]) =>
        convertPropertyToParameter(nestedName, nestedProp, prop.required as string[]),
      );
    }

    return param;
  };

  const convertSchemaToParameters = (schema: Record<string, unknown>): ParameterDefinition[] => {
    if (!schema.properties) return [];
    return Object.entries(schema.properties as Record<string, Record<string, unknown>>).map(
      ([name, prop]) => convertPropertyToParameter(name, prop, schema.required as string[]),
    );
  };

  const nameValue = watch('name');
  const descriptionValue = watch('description');
  const watchDisplayEnabled = watch('display_enabled');
  const watchDisplayType = watch('display_type');

  const generatePreviewJSON = () => {
    const schema = {
      ...convertParametersToSchema(parameters),
      ...(bodyTemplate ? { body_template: bodyTemplate } : {}),
    };
    const headersObj: Record<string, string> = {};
    headerInputs.forEach((h) => {
      if (h.key.trim() && h.value.trim()) headersObj[h.key.trim()] = h.value.trim();
    });

    const data = getValues();
    const result: Record<string, unknown> = {
      name: nameValue || 'tool_name',
      description: descriptionValue || 'Tool description',
      http_method: data.http_method,
      url: data.url || '',
      headers: Object.keys(headersObj).length > 0 ? headersObj : undefined,
      parameters_schema: schema,
      response_mapping: {
        output_path: data.output_path || undefined,
        error_path: data.error_path || undefined,
        selected_fields: buildSelectedFields(data.selected_fields),
      },
    };

    if (data.display_enabled) {
      result.display_config = {
        type: data.display_type,
        array_path: data.display_array_path || undefined,
        fields: {
          title: data.display_field_title ?? '',
          ...(data.display_field_image && { image: data.display_field_image }),
          ...(data.display_field_description && { description: data.display_field_description }),
          ...(data.display_field_price && { price: data.display_field_price }),
          ...(data.display_field_link && { link: data.display_field_link }),
          ...(data.display_field_badge && { badge: data.display_field_badge }),
          ...(data.display_field_file_type && { file_type: data.display_field_file_type }),
        },
        ...(data.display_fixed && { fixed: true }),
        ...(data.display_fallback_image && { fallback_image: data.display_fallback_image }),
        ...(data.whatsapp_link_base && { whatsapp_link_base: data.whatsapp_link_base }),
      };
    }

    const tokenAuth = buildTokenAuth();
    if (tokenAuth) {
      result.token_auth = tokenAuth;
    }

    // Clean undefined values
    Object.keys(result).forEach((key) => {
      if (result[key] === undefined) delete result[key];
    });

    return result;
  };

  const copyPreviewJSON = async () => {
    const json = JSON.stringify(generatePreviewJSON(), null, 2);
    if (!navigator.clipboard?.writeText) {
      alert('Clipboard functionality is not available in this browser.');
      return;
    }
    try {
      await navigator.clipboard.writeText(json);
    } catch (error) {
      console.error('Failed to copy preview JSON to clipboard:', error);
      alert('Failed to copy preview JSON to clipboard. Please try again.');
    }
  };

  const handleFullToolImport = () => {
    try {
      setFullImportError('');
      const parsed = JSON.parse(fullImportJson);

      if (parsed.name) setValue('name', parsed.name);
      if (parsed.description) setValue('description', parsed.description);
      if (parsed.http_method) setValue('http_method', parsed.http_method);
      if (parsed.url) setValue('url', parsed.url);

      if (parsed.response_mapping) {
        if (parsed.response_mapping.output_path)
          setValue('output_path', parsed.response_mapping.output_path);
        if (parsed.response_mapping.error_path)
          setValue('error_path', parsed.response_mapping.error_path);
        if (parsed.response_mapping.selected_fields) {
          const imported = parsed.response_mapping.selected_fields as Array<
            string | { path?: string; as?: string; substr?: [number, number]; add?: number }
          >;
          setValue(
            'selected_fields',
            imported.filter((f): f is string => typeof f === 'string').join(', '),
          );
          setComputedFields(
            imported
              .filter(
                (f): f is { path?: string; as?: string; substr?: [number, number]; add?: number } =>
                  typeof f === 'object' && f !== null,
              )
              .map((f) => ({
                path: f.path ?? '',
                as: f.as ?? '',
                substrStart: f.substr?.[0]?.toString() ?? '',
                substrEnd: f.substr?.[1]?.toString() ?? '',
                add: f.add?.toString() ?? '',
              })),
          );
        }
      }

      if (parsed.display_config) {
        const dc = parsed.display_config;
        setValue('display_enabled', true);
        if (dc.type) setValue('display_type', dc.type);
        if (dc.array_path) setValue('display_array_path', dc.array_path);
        if (dc.fields?.title) setValue('display_field_title', dc.fields.title);
        if (dc.fields?.image) setValue('display_field_image', dc.fields.image);
        if (dc.fields?.description) setValue('display_field_description', dc.fields.description);
        if (dc.fields?.price) setValue('display_field_price', dc.fields.price);
        if (dc.fields?.link) setValue('display_field_link', dc.fields.link);
        if (dc.fields?.badge) setValue('display_field_badge', dc.fields.badge);
        if (dc.fields?.file_type) setValue('display_field_file_type', dc.fields.file_type);
        if (dc.fixed) setValue('display_fixed', true);
        if (dc.fallback_image) setValue('display_fallback_image', dc.fallback_image);
        if (dc.whatsapp_link_base) setValue('whatsapp_link_base', dc.whatsapp_link_base);
      }

      if (parsed.whatsapp_template_name)
        setValue('whatsapp_template_name', parsed.whatsapp_template_name);

      if (parsed.headers && typeof parsed.headers === 'object') {
        const headerEntries = Object.entries(parsed.headers).map(([key, value]) => ({
          key,
          value: String(value),
        }));
        setHeaderInputs(headerEntries);
      }

      if (parsed.token_auth && typeof parsed.token_auth === 'object') {
        const ta = parsed.token_auth as TokenAuth;
        setEnableTokenAuth(true);
        setTokenUrl(ta.token_url ?? '');
        setTokenMethod((ta.token_method as CustomToolFormValues['http_method']) ?? 'GET');
        setTokenPath(ta.token_path ?? '');
        setExpiresInPath(ta.expires_in_path ?? '');
        setInjectHeader(ta.inject_header ?? '');
        setInjectTemplate(ta.inject_template ?? '');
        setTokenHeaderInputs(
          Object.entries(ta.token_headers ?? {}).map(([key, value]) => ({
            key,
            value: String(value),
          })),
        );
        setTokenExtra({
          token_query: ta.token_query,
          token_body: ta.token_body,
          refresh_status_codes: ta.refresh_status_codes,
        });
      } else {
        setEnableTokenAuth(false);
      }

      // Support both `parameters_schema` and `parameters` (shorthand)
      const paramSchema = parsed.parameters_schema || parsed.parameters;
      if (paramSchema?.properties) {
        const params = convertSchemaToParameters(paramSchema);
        setParameters(params);
      }
      if (paramSchema?.body_template) {
        setBodyTemplate(paramSchema.body_template);
      }

      setShowFullImport(false);
      setFullImportJson('');
    } catch (err: unknown) {
      setFullImportError((err as Error).message || 'Invalid JSON format');
    }
  };

  const onFormSubmit = handleSubmit(
    (data) => {
      const validateParameterNames = (params: ParameterDefinition[]): boolean => {
        for (const param of params) {
          if (!param.name?.trim()) return false;
          if (param.properties && !validateParameterNames(param.properties)) return false;
          if (param.items?.properties && !validateParameterNames(param.items.properties))
            return false;
        }
        return true;
      };

      if (!validateParameterNames(parameters)) {
        alert('All parameters must have a name before saving.');
        return;
      }

      if (enableTokenAuth && !tokenUrl.trim()) {
        alert('Token URL is required when token authentication is enabled.');
        return;
      }

      const headersObj: Record<string, string> = {};
      headerInputs.forEach((header) => {
        if (header.key.trim() && header.value.trim()) {
          headersObj[header.key.trim()] = header.value.trim();
        }
      });

      const parametersSchema = {
        ...convertParametersToSchema(parameters),
        ...(bodyTemplate ? { body_template: bodyTemplate } : {}),
      };

      const displayConfig = data.display_enabled
        ? {
            type: data.display_type,
            array_path: data.display_array_path || undefined,
            fields: {
              title: data.display_field_title ?? '',
              ...(data.display_field_image && { image: data.display_field_image }),
              ...(data.display_field_description && {
                description: data.display_field_description,
              }),
              ...(data.display_field_price && { price: data.display_field_price }),
              ...(data.display_field_link && { link: data.display_field_link }),
              ...(data.display_field_badge && { badge: data.display_field_badge }),
              ...(data.display_field_file_type && { file_type: data.display_field_file_type }),
              ...(data.display_field_file_type && { file_type: data.display_field_file_type }),
            },
            ...(data.display_fixed && { fixed: true }),
            ...(data.display_fallback_image && { fallback_image: data.display_fallback_image }),
            ...(data.whatsapp_link_base && { whatsapp_link_base: data.whatsapp_link_base }),
          }
        : null;

      const dto: CreateCustomToolDto = {
        name: data.name,
        description: data.description,
        http_method: data.http_method,
        url: data.url,
        headers: headersObj,
        parameters_schema: parametersSchema,
        response_mapping: {
          output_path: data.output_path || undefined,
          error_path: data.error_path || undefined,
          selected_fields: buildSelectedFields(data.selected_fields),
        },
        is_active: true,
        is_external: data.is_external,
        idempotency: data.idempotency,
        display_config: displayConfig,
        whatsapp_template_name: data.whatsapp_template_name || null,
        token_auth: buildTokenAuth(),
      };

      onSubmit?.(dto);
    },
    (formErrors) => {
      scrollToFirstError(formErrors);
    },
  );

  return (
    <div className="space-y-4">
      {/* Sticky toolbar */}
      <div className="bg-background sticky top-0 z-10 flex items-center justify-between border-b pb-3">
        <h3 className="text-lg font-semibold">
          {initialValues ? 'Edit Custom Tool' : 'Create Custom Tool'}
        </h3>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowFullImport(true)}>
            <FileUp size={16} className="mr-1" />
            Import
          </Button>
          <TestToolDialog
            getToolConfig={getTestToolConfig}
            onFieldSelect={handleFieldSelectAsFilter}
            onSampleFieldsChange={setSampleFilterFields}
            onTopLevelKeysDetected={setDetectedTopLevelKeys}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? (
              <EyeOff size={16} className="mr-1" />
            ) : (
              <Eye size={16} className="mr-1" />
            )}
            {showPreview ? 'Hide' : 'Preview'}
          </Button>
          {showPreview && (
            <Button type="button" variant="outline" size="sm" onClick={copyPreviewJSON}>
              <Copy size={16} className="mr-1" />
              Copy
            </Button>
          )}
        </div>
      </div>

      {/* Full Tool Import Dialog */}
      {showFullImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background mx-4 max-h-[80vh] w-full max-w-2xl overflow-auto rounded-lg shadow-xl">
            <div className="space-y-4 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Import Full Tool from JSON</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowFullImport(false);
                    setFullImportError('');
                  }}
                >
                  ✕
                </Button>
              </div>
              <FormTextarea
                rows={15}
                placeholder="Paste your full tool JSON here..."
                value={fullImportJson}
                onChange={(e) => setFullImportJson(e.target.value)}
              />
              {fullImportError && (
                <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                  {fullImportError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowFullImport(false);
                    setFullImportError('');
                  }}
                >
                  Cancel
                </Button>
                <Button type="button" onClick={handleFullToolImport}>
                  Import Tool
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* JSON Preview */}
      {showPreview && (
        <div className="bg-muted rounded-lg border p-4">
          <pre className="max-h-60 overflow-auto text-xs">
            <code>{JSON.stringify(generatePreviewJSON(), null, 2)}</code>
          </pre>
        </div>
      )}

      {/* Form — organized in collapsible sections */}
      <form className="space-y-4" onSubmit={onFormSubmit}>
        {/* Section 1: Basic Info */}
        <Section title="Basic Information" icon={Settings2}>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Tool Name" htmlFor="name" error={errors.name?.message} required>
                <FormInput
                  id="name"
                  placeholder="e.g., search_vehicles"
                  error={!!errors.name}
                  {...register('name')}
                />
                <p className="text-muted-foreground mt-1 text-xs">snake_case only</p>
              </FormField>

              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label="Method"
                  htmlFor="http_method"
                  error={errors.http_method?.message}
                  required
                >
                  <FormSelect id="http_method" {...register('http_method')}>
                    {HTTP_METHOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField label="URL" htmlFor="url" error={errors.url?.message} required>
                  <FormInput
                    id="url"
                    type="url"
                    placeholder="https://api.example.com/{id}"
                    error={!!errors.url}
                    {...register('url')}
                  />
                </FormField>
              </div>
            </div>

            {pathVariables.length > 0 && (
              <p className="text-muted-foreground text-xs">
                Path variables: {pathVariables.map((v) => `{${v}}`).join(', ')}
              </p>
            )}

            <FormField
              label="Description"
              htmlFor="description"
              error={errors.description?.message}
              required
            >
              <FormTextarea
                id="description"
                rows={2}
                placeholder="What this tool does and when the AI should use it..."
                error={!!errors.description}
                {...register('description')}
              />
            </FormField>

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" {...register('is_external')} className="mt-0.5 h-4 w-4" />
              <span>
                External API
                <span className="text-muted-foreground ml-2 text-xs">
                  Skip injecting internal session data (callId, callSid, contactId, campaignId).
                  Enable for third-party APIs that reject unknown parameters.
                </span>
              </span>
            </label>

            <FormField label="Idempotency" htmlFor="idempotency">
              <select
                id="idempotency"
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                {...register('idempotency')}
              >
                <option value="none">None — runs on every invocation</option>
                <option value="per_call">
                  Once per conversation — repeat calls return the first result
                </option>
                <option value="per_call_and_args">
                  Once per conversation + arguments — identical args return the first result
                </option>
              </select>
              <p className="text-muted-foreground mt-1 text-xs">
                Guards tools that must not fire twice in one conversation (e.g. a confirmation or
                flag webhook). Enforced by the engine, across server instances.
              </p>
            </FormField>
          </div>
        </Section>

        {/* Section 2: Headers */}
        <Section
          title="Headers"
          icon={Globe}
          defaultOpen={headerInputs.length > 0}
          badge={headerInputs.length || undefined}
        >
          <div className="space-y-2">
            {headerInputs.map((header, index) => (
              <div key={index} className="flex gap-2">
                <FormInput
                  placeholder="Header name"
                  value={header.key}
                  onChange={(e) => updateHeaderRow(index, 'key', e.target.value)}
                  className="text-sm"
                />
                <FormInput
                  placeholder="Header value"
                  value={header.value}
                  onChange={(e) => updateHeaderRow(index, 'value', e.target.value)}
                  className="text-sm"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeHeaderRow(index)}
                  className="text-destructive h-9 w-9 shrink-0 p-0"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addHeaderRow}>
              <Plus size={14} className="mr-1" />
              Add Header
            </Button>
          </div>
        </Section>

        {/* Section 3: Parameters */}
        <Section title="Parameters" icon={SlidersHorizontal} badge={parameters.length || undefined}>
          <div className="space-y-2">
            {/* Add buttons at the top */}
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={addParameter}>
                <Plus size={14} className="mr-1" />
                Add Parameter
              </Button>
              <JSONImportDialog
                onImport={(params, bt) => {
                  setParameters(params);
                  if (bt) setBodyTemplate(bt);
                }}
              />
            </div>

            {parameters.length === 0 ? (
              <div className="bg-muted/30 rounded-md border border-dashed p-4 text-center">
                <p className="text-muted-foreground text-sm">
                  No parameters yet. Add parameters to define inputs for this tool.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {parameters.map((param, index) => (
                  <ParameterEditor
                    key={index}
                    parameter={param}
                    onChange={(updated) => updateParameter(index, updated)}
                    onRemove={() => removeParameter(index)}
                    pathVariables={pathVariables}
                    availableFilterFields={sampleFilterFields}
                    autoExpand={index === newParamIndex}
                  />
                ))}
                <div ref={parametersEndRef} />
              </div>
            )}
          </div>
        </Section>

        {/* Section 4: Response Mapping */}
        <Section title="Response Mapping" icon={Settings2} defaultOpen={false}>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                label="Output Path"
                htmlFor="output_path"
                error={errors.output_path?.message}
              >
                <FormInput
                  id="output_path"
                  placeholder="e.g., data.result"
                  {...register('output_path')}
                  className="text-sm"
                />
              </FormField>
              <FormField label="Error Path" htmlFor="error_path" error={errors.error_path?.message}>
                <FormInput
                  id="error_path"
                  placeholder="e.g., error.message"
                  {...register('error_path')}
                  className="text-sm"
                />
              </FormField>
            </div>
            <FormField
              label="Selected Fields"
              htmlFor="selected_fields"
              error={errors.selected_fields?.message}
            >
              <FormInput
                id="selected_fields"
                placeholder="e.g., data.id, data.name, status"
                {...register('selected_fields')}
                className="text-sm"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Comma-separated dot-notation paths to extract from responses
              </p>
              {detectedTopLevelKeys.length > 0 && (
                <div className="mt-2">
                  <p className="text-muted-foreground mb-1 text-xs">
                    Detected keys (click to add):
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {detectedTopLevelKeys.map((key) => {
                      const currentValue = watch('selected_fields') || '';
                      const existing = currentValue
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const isSelected = existing.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            const next = isSelected
                              ? existing.filter((f) => f !== key)
                              : [...existing, key];
                            setValue('selected_fields', next.join(', '));
                          }}
                          className={`rounded-md border px-2 py-0.5 text-xs transition-colors ${
                            isSelected
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-muted hover:bg-muted/70'
                          }`}
                        >
                          {isSelected ? '✓ ' : '+ '}
                          {key}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </FormField>

            {/* Computed fields — derived values ({ path, as, substr }) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Computed Fields</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setComputedFields([
                      ...computedFields,
                      { path: '', as: '', substrStart: '', substrEnd: '', add: '' },
                    ])
                  }
                >
                  <Plus size={14} className="mr-1" />
                  Add
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">
                Derive a value from a response path: optionally take a substring (start/end indices)
                and/or add a number to it, then output it under a new name. Useful for building
                image URLs from an id prefix, or a price plus a fixed admin fee.
              </p>
              {computedFields.map((cf, index) => (
                <div key={index} className="flex flex-wrap items-end gap-2 rounded-md border p-2">
                  <div className="flex-1 space-y-1" style={{ minWidth: 140 }}>
                    <label className="text-muted-foreground text-xs">Path</label>
                    <FormInput
                      placeholder="e.g., results.0.id"
                      value={cf.path}
                      onChange={(e) => {
                        const next = [...computedFields];
                        next[index] = { ...next[index], path: e.target.value };
                        setComputedFields(next);
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="flex-1 space-y-1" style={{ minWidth: 120 }}>
                    <label className="text-muted-foreground text-xs">Output as</label>
                    <FormInput
                      placeholder="e.g., id_prefix"
                      value={cf.as}
                      onChange={(e) => {
                        const next = [...computedFields];
                        next[index] = { ...next[index], as: e.target.value };
                        setComputedFields(next);
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="w-20 space-y-1">
                    <label className="text-muted-foreground text-xs">Sub start</label>
                    <FormInput
                      type="number"
                      placeholder="0"
                      value={cf.substrStart}
                      onChange={(e) => {
                        const next = [...computedFields];
                        next[index] = { ...next[index], substrStart: e.target.value };
                        setComputedFields(next);
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="w-20 space-y-1">
                    <label className="text-muted-foreground text-xs">Sub end</label>
                    <FormInput
                      type="number"
                      placeholder="2"
                      value={cf.substrEnd}
                      onChange={(e) => {
                        const next = [...computedFields];
                        next[index] = { ...next[index], substrEnd: e.target.value };
                        setComputedFields(next);
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="w-24 space-y-1">
                    <label className="text-muted-foreground text-xs">Add</label>
                    <FormInput
                      type="number"
                      placeholder="299"
                      value={cf.add}
                      onChange={(e) => {
                        const next = [...computedFields];
                        next[index] = { ...next[index], add: e.target.value };
                        setComputedFields(next);
                      }}
                      className="text-sm"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setComputedFields(computedFields.filter((_, i) => i !== index))}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 5: Widget Display */}
        <Section title="Widget Display" icon={Layout} defaultOpen={!!initialValues?.display_config}>
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('display_enabled')} className="h-4 w-4" />
              Show results in widget
            </label>

            {watchDisplayEnabled && (
              <div className="space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <FormField label="Display Type" htmlFor="display_type">
                    <FormSelect id="display_type" {...register('display_type')}>
                      <option value="carousel">Carousel</option>
                      <option value="cards">Cards</option>
                      <option value="notice">Notice</option>
                      <option value="badges">Badges</option>
                      <option value="links">Links</option>
                      <option value="files">Files (send PDFs / images)</option>
                    </FormSelect>
                  </FormField>

                  {watchDisplayType !== 'notice' && (
                    <FormField label="Array Path" htmlFor="display_array_path">
                      <FormInput
                        id="display_array_path"
                        placeholder="e.g. data.vehicles"
                        {...register('display_array_path')}
                        className="text-sm"
                      />
                    </FormField>
                  )}
                </div>

                <p className="text-muted-foreground text-xs">
                  Use {'{{field_name}}'} for dynamic values
                </p>

                <div className="grid gap-3 md:grid-cols-2">
                  <FormField label="Title" htmlFor="display_field_title" required>
                    <FormInput
                      id="display_field_title"
                      placeholder="e.g. {{make}} {{model}}"
                      {...register('display_field_title')}
                      className="text-sm"
                    />
                  </FormField>

                  <FormField label="Description" htmlFor="display_field_description">
                    <FormInput
                      id="display_field_description"
                      placeholder="e.g. {{year}} - {{mileage}} miles"
                      {...register('display_field_description')}
                      className="text-sm"
                    />
                  </FormField>

                  {(watchDisplayType === 'carousel' || watchDisplayType === 'cards') && (
                    <>
                      <FormField label="Image Field" htmlFor="display_field_image">
                        <FormInput
                          id="display_field_image"
                          placeholder="e.g. image_url"
                          {...register('display_field_image')}
                          className="text-sm"
                        />
                      </FormField>
                      <FormField label="Price Field" htmlFor="display_field_price">
                        <FormInput
                          id="display_field_price"
                          placeholder="e.g. price_formatted"
                          {...register('display_field_price')}
                          className="text-sm"
                        />
                      </FormField>
                    </>
                  )}

                  <FormField label="Link" htmlFor="display_field_link">
                    <FormInput
                      id="display_field_link"
                      placeholder="e.g. https://example.com/{{slug}}"
                      {...register('display_field_link')}
                      className="text-sm"
                    />
                  </FormField>

                  {watchDisplayType === 'files' && (
                    <FormField label="File Type Field" htmlFor="display_field_file_type">
                      <FormInput
                        id="display_field_file_type"
                        placeholder='e.g. send_as ("image" or "document")'
                        {...register('display_field_file_type')}
                        className="text-sm"
                      />
                    </FormField>
                  )}

                  {watchDisplayType === 'badges' && (
                    <FormField label="Badge Field" htmlFor="display_field_badge">
                      <FormInput
                        id="display_field_badge"
                        placeholder="e.g. status"
                        {...register('display_field_badge')}
                        className="text-sm"
                      />
                    </FormField>
                  )}
                </div>

                {(watchDisplayType === 'carousel' || watchDisplayType === 'cards') && (
                  <FormField label="Fallback Image URL" htmlFor="display_fallback_image">
                    <FormInput
                      id="display_fallback_image"
                      placeholder="https://example.com/placeholder.png"
                      {...register('display_fallback_image')}
                      className="text-sm"
                    />
                    <p className="text-muted-foreground mt-1 text-xs">
                      Used when an item&apos;s image can&apos;t be resolved (e.g. a vehicle with no
                      photo). Must be a public JPG/PNG — required for WhatsApp carousels to render.
                    </p>
                  </FormField>
                )}

                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register('display_fixed')}
                    className="mt-0.5 h-4 w-4"
                  />
                  <span>
                    Keep on screen (pin)
                    <span className="text-muted-foreground block text-[11px]">
                      Stays visible when other tool results appear; new results stack beneath it.
                      Only applies in overlay display mode.
                    </span>
                  </span>
                </label>

                <div className="border-t pt-3">
                  <FormField label="WhatsApp Template Name" htmlFor="whatsapp_template_name">
                    <FormInput
                      id="whatsapp_template_name"
                      placeholder="e.g. vehicle_search_results"
                      {...register('whatsapp_template_name')}
                      className="text-sm"
                    />
                    <p className="text-muted-foreground mt-1 text-xs">
                      Link an approved WhatsApp carousel template to send scrollable cards instead
                      of individual messages during WhatsApp calls.
                    </p>
                  </FormField>
                  <FormField label="WhatsApp Link Base" htmlFor="whatsapp_link_base">
                    <FormInput
                      id="whatsapp_link_base"
                      placeholder="e.g. https://www.example.com/vehicle/"
                      {...register('whatsapp_link_base')}
                      className="text-sm"
                    />
                    <p className="text-muted-foreground mt-1 text-xs">
                      Static URL prefix to strip from the link before passing it as the button
                      parameter. E.g. if the link is &quot;https://example.com/vehicle/BMW/1&quot;
                      and the template URL is &quot;https://example.com/vehicle/{'{{1}}'}&quot;, set
                      this to &quot;https://example.com/vehicle/&quot;.
                    </p>
                  </FormField>
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Section 6: Token Authentication */}
        <Section
          title="Token Authentication (optional)"
          icon={KeyRound}
          defaultOpen={enableTokenAuth}
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-xs">
              For APIs that issue a short-lived bearer token from a separate endpoint. The token is
              fetched, cached and refreshed automatically.
            </p>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enableTokenAuth}
                onChange={(e) => setEnableTokenAuth(e.target.checked)}
                className="h-4 w-4"
              />
              Enable token authentication
            </label>

            {enableTokenAuth && (
              <div className="space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <FormField label="Token URL" htmlFor="token_url" required>
                    <FormInput
                      id="token_url"
                      placeholder="https://api.example.com/token"
                      value={tokenUrl}
                      onChange={(e) => setTokenUrl(e.target.value)}
                      className="text-sm"
                    />
                  </FormField>

                  <FormField label="Token Method" htmlFor="token_method">
                    <FormSelect
                      id="token_method"
                      value={tokenMethod}
                      onChange={(e) =>
                        setTokenMethod(e.target.value as CustomToolFormValues['http_method'])
                      }
                    >
                      {HTTP_METHOD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </FormSelect>
                  </FormField>
                </div>

                {/* Token request headers — same key/value editor as request Headers */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Token Headers</label>
                  {tokenHeaderInputs.map((header, index) => (
                    <div key={index} className="flex gap-2">
                      <FormInput
                        placeholder="Header name"
                        value={header.key}
                        onChange={(e) => updateTokenHeaderRow(index, 'key', e.target.value)}
                        className="text-sm"
                      />
                      <FormInput
                        placeholder="Header value"
                        value={header.value}
                        onChange={(e) => updateTokenHeaderRow(index, 'value', e.target.value)}
                        className="text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTokenHeaderRow(index)}
                        className="text-destructive h-9 w-9 shrink-0 p-0"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addTokenHeaderRow}>
                    <Plus size={14} className="mr-1" />
                    Add Header
                  </Button>
                  <p className="text-muted-foreground text-xs">
                    Sent to the token endpoint. Use {'{{env.NAME}}'} to resolve a secret from the
                    server environment.
                  </p>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <FormField label="Token Path" htmlFor="token_path">
                    <FormInput
                      id="token_path"
                      placeholder="token"
                      value={tokenPath}
                      onChange={(e) => setTokenPath(e.target.value)}
                      className="text-sm"
                    />
                    <p className="text-muted-foreground mt-1 text-xs">
                      Dot-path to the token in the auth response (defaults to &quot;token&quot;).
                    </p>
                  </FormField>

                  <FormField label="Expires-in Path" htmlFor="expires_in_path">
                    <FormInput
                      id="expires_in_path"
                      placeholder="expiresIn"
                      value={expiresInPath}
                      onChange={(e) => setExpiresInPath(e.target.value)}
                      className="text-sm"
                    />
                    <p className="text-muted-foreground mt-1 text-xs">
                      Dot-path to the token lifetime in seconds (used to cache/refresh).
                    </p>
                  </FormField>

                  <FormField label="Inject Header" htmlFor="inject_header">
                    <FormInput
                      id="inject_header"
                      placeholder="Authorization"
                      value={injectHeader}
                      onChange={(e) => setInjectHeader(e.target.value)}
                      className="text-sm"
                    />
                  </FormField>

                  <FormField label="Inject Template" htmlFor="inject_template">
                    <FormInput
                      id="inject_template"
                      placeholder="Bearer {{token}}"
                      value={injectTemplate}
                      onChange={(e) => setInjectTemplate(e.target.value)}
                      className="text-sm"
                    />
                  </FormField>
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Submit */}
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={isLoading} className="min-w-32">
            <Save size={16} className="mr-2" />
            {isLoading ? 'Saving...' : submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};
